import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext(null);

export const ThemeProvider = ({ children }) => {
    const [theme, setTheme] = useState(() => {
        const saved = localStorage.getItem('theme');
        // If user has saved preference, use it; otherwise use 'system'
        return saved || 'system';
    });
    
    const [resolvedTheme, setResolvedTheme] = useState('light');

    // Listen for system preference changes
    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        
        const handleChange = (e) => {
            if (theme === 'system') {
                setResolvedTheme(e.matches ? 'dark' : 'light');
            }
        };
        
        // Set initial resolved theme
        if (theme === 'system') {
            setResolvedTheme(mediaQuery.matches ? 'dark' : 'light');
        } else {
            setResolvedTheme(theme);
        }
        
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [theme]);

    // Apply theme to document
    useEffect(() => {
        const root = window.document.documentElement;
        
        // Add no-transition class to prevent flash
        root.classList.add('no-transition');
        
        root.classList.remove('light', 'dark');
        root.classList.add(resolvedTheme);
        localStorage.setItem('theme', theme);
        
        // Remove no-transition after a tick
        requestAnimationFrame(() => {
            root.classList.remove('no-transition');
        });
    }, [resolvedTheme, theme]);

    const setThemeMode = (mode) => {
        // mode can be 'light', 'dark', or 'system'
        setTheme(mode);
        if (mode !== 'system') {
            setResolvedTheme(mode);
        }
    };

    const toggleTheme = () => {
        // Cycle through: system -> light -> dark -> system
        if (theme === 'system') {
            setThemeMode('light');
        } else if (theme === 'light') {
            setThemeMode('dark');
        } else {
            setThemeMode('system');
        }
    };

    return (
        <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme: setThemeMode, toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

export default ThemeContext;
